import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Clock3, IndianRupee, MapPin, Wrench } from "lucide-react";
import { toast } from "sonner";

import api from "../../lib/api";
import { residentServiceIcons } from "../../config/residentServiceIcons";
import { Button } from "../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Loading } from "../../components/shared";


function dateText(value) {
  return value ? new Date(value).toLocaleString("en-IN") : "";
}


export default function ResidentDoorstepServices() {
  const [catalogue, setCatalogue] = useState(null);
  const [requests, setRequests] = useState([]);
  const [selectedKey, setSelectedKey] = useState("");
  const [service, setService] = useState(null);
  const [request, setRequest] = useState(null);
  const [draft, setDraft] = useState({ description: "", preferred_time_note: "", contact_phone: "" });

  const load = async () => {
    const [catalogueResponse, requestResponse] = await Promise.all([
      api.get("/resident-doorstep/services"),
      api.get("/resident-doorstep/requests"),
    ]);
    setCatalogue(catalogueResponse.data);
    setRequests(requestResponse.data.requests || []);
  };

  useEffect(() => {
    load().catch(() => {
      setCatalogue({ categories: [], services: [] });
      setRequests([]);
    });
  }, []);

  const categories = useMemo(() => catalogue?.catalogue || [], [catalogue]);
  const selectedCategory = useMemo(
    () => categories.find((item) => item.key === selectedKey) || null,
    [categories, selectedKey],
  );
  const offerings = selectedCategory?.service?.offerings || [];

  const submit = async () => {
    if (!service || !draft.description.trim()) {
      toast.error("Please describe what you need.");
      return;
    }
    try {
      await api.post("/resident-doorstep/requests", {
        service_id: service.parent.service_id,
        offering_id: service.offering.offering_id,
        ...draft,
      });
      toast.success("Request received");
      setService(null);
      setDraft({ description: "", preferred_time_note: "", contact_phone: "" });
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not submit the request");
    }
  };

  if (!catalogue) return <Loading />;

  return (
    <div className="space-y-8" data-testid="resident-doorstep-services-page">
      <header className="border-b border-slate-200 pb-7">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-sky-700">
          Home support
        </p>
        <div className="mt-3 flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="font-display text-4xl font-bold text-slate-950">Doorstep Services</h1>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600">
              Explore only services configured for residents. A submitted request is received for review;
              it does not confirm a technician or payment.
            </p>
          </div>
          <Link
            className="inline-flex items-center gap-2 text-sm font-semibold text-sky-800 hover:text-sky-950"
            data-testid="resident-doorstep-legacy-pilot-link"
            to="/resident/technician-services"
          >
            Pilot technician bookings
            <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>
      </header>
      <section
        className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6"
        data-testid="resident-doorstep-category-catalogue"
      >
        {categories.map((item) => {
          const Icon = residentServiceIcons[item.icon] || Wrench;
          const optionCount = item.service?.offerings?.length || 0;
          return (
            <button
              className={`border p-4 text-left transition-[border-color,background-color,transform] duration-200 hover:-translate-y-0.5 ${
                selectedKey === item.key
                  ? "!border-[#075985] !bg-[#075985] !text-white"
                  : "border-slate-200 bg-white text-slate-800 hover:border-sky-300"
              }`}
              data-testid={`resident-doorstep-category-${item.key}`}
              key={item.key}
              onClick={() => setSelectedKey(item.key)}
              type="button"
            >
              <Icon
                className={`h-5 w-5 ${
                  selectedKey === item.key ? "text-white" : "text-orange-600"
                }`}
              />
              <span className="mt-4 block text-sm font-semibold leading-5">{item.name}</span>
              <span
                className={`mt-2 block text-xs ${
                  selectedKey === item.key ? "text-sky-100" : "text-slate-500"
                }`}
              >
                {optionCount
                  ? `${optionCount} option${optionCount === 1 ? "" : "s"}`
                  : "Not configured"}
              </span>
            </button>
          );
        })}
      </section>
      <section data-testid="resident-doorstep-catalogue">
        {selectedCategory ? (
          <>
            <div className="mb-5 border-b border-slate-200 pb-4">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-sky-700">
                Service options
              </p>
              <h2 className="mt-1 font-display text-3xl font-bold text-slate-950">
                {selectedCategory.name}
              </h2>
              <p className="mt-2 text-sm text-slate-600">
                Select the exact service you need. Rates and GST appear only when configured.
              </p>
            </div>
            <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
              {offerings.length ? (
                offerings.map((item) => (
                  <article
                    className="flex min-h-[250px] flex-col border border-slate-200 bg-white p-6 shadow-sm transition-[border-color,box-shadow,transform] duration-200 hover:-translate-y-1 hover:border-sky-200 hover:shadow-md"
                    data-testid={`resident-doorstep-option-${item.offering_id}`}
                    key={item.offering_id}
                  >
                    <span className="border border-orange-200 bg-orange-50 p-2 text-orange-700">
                      <Wrench className="h-5 w-5" />
                    </span>
                    <h3 className="mt-5 font-display text-2xl font-bold text-slate-950">
                      {item.name}
                    </h3>
                    <p className="mt-2 flex-1 text-sm leading-6 text-slate-600">
                      {item.short_description || "Service details are available on request."}
                    </p>
                    <div className="mt-5 border-t border-slate-100 pt-4">
                      <div className="flex items-center gap-2 text-lg font-bold text-slate-950">
                        {item.price.amount !== null && (
                          <IndianRupee className="h-4 w-4 text-orange-600" />
                        )}
                        {item.price.label.replace("₹", "")}
                      </div>
                      <p className="mt-1 text-xs text-slate-500">{item.price.gst_label}</p>
                    </div>
                    <Button
                      className="mt-5 w-full"
                      data-testid={`resident-doorstep-open-${item.offering_id}`}
                      disabled={!item.request_enabled}
                      onClick={() => setService({ parent: selectedCategory.service, offering: item })}
                    >
                      {item.request_enabled ? "Request service" : "Requests unavailable"}
                    </Button>
                  </article>
                ))
              ) : (
                <div
                  className="border border-dashed border-slate-300 bg-white p-8 md:col-span-2 xl:col-span-3"
                  data-testid="resident-doorstep-options-empty"
                >
                  <Wrench className="h-6 w-6 text-slate-400" />
                  <h3 className="mt-3 font-display text-xl font-semibold text-slate-900">
                    No options are available
                  </h3>
                  <p className="mt-2 text-sm text-slate-600">
                    This service category has not been enabled with resident options.
                  </p>
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="border border-dashed border-slate-300 bg-white p-8" data-testid="resident-doorstep-empty">
            <Wrench className="h-6 w-6 text-slate-400" />
            <h2 className="mt-3 font-display text-xl font-semibold text-slate-900">
              Choose a service category
            </h2>
            <p className="mt-2 text-sm text-slate-600">
              Available service options will appear here when selected.
            </p>
          </div>
        )}
      </section>
      <section className="border-t border-slate-200 pt-8" data-testid="resident-doorstep-request-history">
        <div className="flex items-center gap-2">
          <Clock3 className="h-5 w-5 text-sky-700" />
          <h2 className="font-display text-2xl font-bold text-slate-950">My service requests</h2>
        </div>
        <div className="mt-4 space-y-3">
          {requests.length ? requests.map((item) => (
            <button
              className="flex w-full flex-wrap items-center justify-between gap-3 border border-slate-200 bg-white p-4 text-left transition-[border-color,background-color] hover:border-sky-300 hover:bg-sky-50"
              data-testid={`resident-doorstep-request-${item.request_id}`}
              key={item.request_id}
              onClick={() => setRequest(item)}
              type="button"
            >
              <span>
                <span className="block text-sm font-semibold text-slate-950">
                  {item.offering?.name || item.service.name}
                </span>
                <span className="mt-1 block text-xs text-slate-500">
                  {dateText(item.created_at)} · Unit {item.unit_number}
                </span>
              </span>
              <span className="border border-sky-200 bg-sky-50 px-3 py-1 text-xs font-semibold text-sky-800">
                Request received
              </span>
            </button>
          )) : (
            <p className="border border-dashed border-slate-300 bg-white p-6 text-sm text-slate-600" data-testid="resident-doorstep-request-empty">
              You have not submitted a doorstep service request.
            </p>
          )}
        </div>
      </section>
      <Dialog open={Boolean(service)} onOpenChange={(open) => !open && setService(null)}>
        <DialogContent className="max-h-[90vh] max-w-xl overflow-y-auto" data-testid="resident-doorstep-request-dialog">
          <DialogHeader>
            <DialogTitle className="pr-8 font-display text-2xl">
              Request {service?.offering?.name}
            </DialogTitle>
            <DialogDescription>
              Submit a request for review. It does not confirm technician assignment or payment.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="border border-sky-100 bg-sky-50 p-4 text-sm text-sky-950">
              <p className="font-semibold">{service?.offering?.price.label}</p>
              <p className="mt-1 text-xs">{service?.offering?.price.gst_label}</p>
            </div>
            {service?.offering?.inclusions && (
              <Info label="Includes" value={service.offering.inclusions} />
            )}
            {service?.offering?.exclusions && (
              <Info label="Excludes" value={service.offering.exclusions} />
            )}
            <div>
              <Label htmlFor="resident-doorstep-description">What do you need?</Label>
              <Textarea
                data-testid="resident-doorstep-description"
                id="resident-doorstep-description"
                onChange={(event) => setDraft({ ...draft, description: event.target.value })}
                value={draft.description}
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="resident-doorstep-time">Preferred time</Label>
                <Input
                  data-testid="resident-doorstep-time"
                  id="resident-doorstep-time"
                  onChange={(event) => setDraft({ ...draft, preferred_time_note: event.target.value })}
                  placeholder="For example, weekday evening"
                  value={draft.preferred_time_note}
                />
              </div>
              <div>
                <Label htmlFor="resident-doorstep-phone">Contact number</Label>
                <Input
                  data-testid="resident-doorstep-phone"
                  id="resident-doorstep-phone"
                  onChange={(event) => setDraft({ ...draft, contact_phone: event.target.value })}
                  value={draft.contact_phone}
                />
              </div>
            </div>
            <p className="flex items-start gap-2 text-xs leading-5 text-slate-600">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-slate-500" />
              Your active society and unit are securely attached to this request.
            </p>
          </div>
          <DialogFooter>
            <Button data-testid="resident-doorstep-submit" onClick={submit}>Submit request</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={Boolean(request)} onOpenChange={(open) => !open && setRequest(null)}>
        <DialogContent className="max-w-xl" data-testid="resident-doorstep-request-detail">
          <DialogHeader>
            <DialogTitle className="pr-8 font-display text-2xl">
              {request?.offering?.name || request?.service?.name}
            </DialogTitle>
            <DialogDescription>
              Your submitted Doorstep Service request details.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 text-sm text-slate-700">
            <div className="border border-sky-200 bg-sky-50 p-4">
              <p className="font-semibold text-sky-950">Request received</p>
              <p className="mt-1 text-xs text-sky-800">This request has not been assigned to a technician.</p>
            </div>
            <Info label="Details" value={request?.description} />
            {request?.preferred_time_note && <Info label="Preferred time" value={request.preferred_time_note} />}
            {request?.contact_phone && <Info label="Contact number" value={request.contact_phone} />}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function Info({ label, value }) {
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">{label}</p>
      <p className="mt-1 whitespace-pre-wrap text-sm leading-6 text-slate-800">{value}</p>
    </div>
  );
}